import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ListResponse } from '@lib/api';
import { track } from '@lib/posthog';
import type { Teklif, CreateTeklifInput, ListTeklifQuery } from '@/types/teklif';

const QUERY_KEY = 'teklifler';

export function useTeklifler(query: ListTeklifQuery = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: () =>
      api
        .get('teklifler', {
          searchParams: Object.entries(query).reduce((acc, [k, v]) => {
            if (v !== undefined && v !== '') acc[k] = String(v);
            return acc;
          }, {} as Record<string, string>),
        })
        .json<ListResponse<Teklif>>(),
  });
}

export function useTeklif(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () =>
      api.get(`teklifler/${id}`).json<{ data: Teklif }>().then((r) => r.data),
    enabled: !!id,
  });
}

export function useTeklifFirsattanTaslak() {
  return useMutation({
    mutationFn: (firsatId: string) =>
      api
        .get(`teklifler/firsattan-taslak/${firsatId}`)
        .json<{ data: Partial<CreateTeklifInput> }>()
        .then((r) => r.data),
  });
}

/** Tek tıkla fırsattan teklif oluştur — kalemleri ve müşteriyi otomatik
 *  kopyalar, durum=TASLAK ile yaratır. */
export function useFirsattanTeklifOlustur() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (firsatId: string) =>
      api
        .post(`teklifler/firsattan-olustur/${firsatId}`)
        .json<{ data: Teklif }>(),
    onSuccess: (res) => {
      track('teklif_olusturuldu', { teklif_id: res.data.id });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: ['firsatlar'] });
    },
  });
}

export function useCreateTeklif() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTeklifInput) =>
      api.post('teklifler', { json: input }).json<{ data: Teklif }>().then((r) => r.data),
    onSuccess: (data) => {
      track('teklif_olusturuldu', { teklif_id: data.id });
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateTeklif(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateTeklifInput>) =>
      api.patch(`teklifler/${id}`, { json: input }).json<{ data: Teklif }>().then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

export function useDeleteTeklif() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`teklifler/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}
